import { useState, useCallback } from 'react';
import { usePedidos, useAtualizarStatusPedido } from '@/hooks/usePedidos';
import { Badge } from '@/components/ui/Badge';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { Pedido, StatusPedido } from '@/types';
import { Link } from 'react-router-dom';
import { Eye, ChevronRight, Clock, DollarSign, User, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

const COLUNAS: { status: StatusPedido; label: string; color: string; bg: string; border: string }[] = [
  { status: 'NOVO' as StatusPedido, label: 'Novo', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
  { status: 'PAGO_CONFIRMADO' as StatusPedido, label: 'Pago ✓', color: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-indigo-200' },
  { status: 'PRODUCAO' as StatusPedido, label: 'Produção', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
  { status: 'AGUARDANDO_SEPARACAO' as StatusPedido, label: 'A Separar', color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200' },
  { status: 'PRONTO_EXPEDICAO' as StatusPedido, label: 'Pronto / NF', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  { status: 'ENVIADO' as StatusPedido, label: 'Enviado', color: 'text-gray-700', bg: 'bg-gray-50', border: 'border-gray-200' },
];

// Mapa dos próximos status válidos para avançar com um clique
const PROXIMO_STATUS: Partial<Record<StatusPedido, StatusPedido>> = {
  NOVO: 'PAGO_CONFIRMADO' as StatusPedido,
  PAGO_CONFIRMADO: 'PRODUCAO' as StatusPedido,
  PRODUCAO: 'AGUARDANDO_SEPARACAO' as StatusPedido,
  AGUARDANDO_SEPARACAO: 'PRONTO_EXPEDICAO' as StatusPedido,
  PRONTO_EXPEDICAO: 'ENVIADO' as StatusPedido,
  ENVIADO: 'ENTREGUE' as StatusPedido,
};

function PedidoCard({ pedido, onAvancar, isLoading }: { pedido: Pedido; onAvancar: (id: string, status: StatusPedido) => void; isLoading: boolean }) {
  const proximoStatus = PROXIMO_STATUS[pedido.status];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-all p-3 space-y-3 group"
    >
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs font-bold text-primary">{pedido.numeroPedido}</span>
        <Link to={`/admin/pedidos/${pedido.id}`}>
          <Eye className="w-4 h-4 text-gray-300 group-hover:text-gray-600 transition-colors" />
        </Link>
      </div>

      <div className="flex items-center gap-2 text-xs text-gray-600">
        <User className="w-3 h-3 shrink-0" />
        <span className="truncate font-medium">{pedido.cliente?.nome}</span>
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {formatDate(pedido.createdAt)}
        </div>
        <div className="flex items-center gap-1 font-bold text-gray-800">
          <DollarSign className="w-3 h-3" />
          {formatCurrency(pedido.faturamentoTotal)}
        </div>
      </div>

      <Badge variant="secondary" className="text-[10px] px-2 py-0.5">{pedido.origem}</Badge>

      {proximoStatus && (
        <button
          onClick={() => onAvancar(pedido.id, proximoStatus)}
          disabled={isLoading}
          className={cn(
            'w-full flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg',
            'text-[10px] font-black uppercase tracking-wider transition-all border',
            'bg-gray-50 border-gray-100 text-gray-500',
            'hover:bg-primary hover:border-primary hover:text-white',
            'disabled:opacity-40 disabled:cursor-wait'
          )}
        >
          Avançar
          <ArrowRight className="w-3 h-3" />
        </button>
      )}
    </motion.div>
  );
}

export function KanbanPedidos() {
  const { data: pedidos, isLoading } = usePedidos({});
  const atualizar = useAtualizarStatusPedido();
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const handleAvancar = useCallback(async (id: string, status: StatusPedido) => {
    setLoadingId(id);
    try {
      await atualizar.mutateAsync({ id, status });
    } finally {
      setLoadingId(null);
    }
  }, [atualizar]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 animate-pulse">
        {COLUNAS.map((col) => (
          <div key={col.status} className="bg-gray-50 rounded-2xl h-64" />
        ))}
      </div>
    );
  }

  const pedidosPorStatus = (status: StatusPedido) =>
    (pedidos as Pedido[] | undefined)?.filter((p) => p.status === status) ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Pipeline de Pedidos</h2>
          <p className="text-sm text-gray-500">Clique em "Avançar" para mover o pedido para a próxima etapa</p>
        </div>
        <div className="text-xs text-gray-400 flex items-center gap-1">
          {COLUNAS.map((c, i) => (
            <span key={c.status} className="flex items-center gap-1">
              <span className={cn('px-2 py-0.5 rounded-full font-bold', c.bg, c.color)}>{c.label}</span>
              {i < COLUNAS.length - 1 && <ChevronRight className="w-3 h-3" />}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 overflow-x-auto pb-4">
        {COLUNAS.map((col) => {
          const itens = pedidosPorStatus(col.status);
          return (
            <div
              key={col.status}
              className={cn('rounded-2xl border p-3 min-h-[300px] flex flex-col gap-3', col.bg, col.border)}
            >
              <div className="flex items-center justify-between mb-1">
                <h3 className={cn('text-xs font-black uppercase tracking-wider', col.color)}>{col.label}</h3>
                <span className={cn('text-xs font-black w-5 h-5 rounded-full flex items-center justify-center', col.color, 'bg-white/70')}>
                  {itens.length}
                </span>
              </div>

              <AnimatePresence mode="popLayout">
                {itens.map((p) => (
                  <PedidoCard
                    key={p.id}
                    pedido={p}
                    onAvancar={handleAvancar}
                    isLoading={loadingId === p.id}
                  />
                ))}
              </AnimatePresence>

              {itens.length === 0 && (
                <div className="flex-1 flex items-center justify-center">
                  <p className="text-xs text-gray-300 italic">Vazio</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
